/**
 * Editor для doc.reconciliation_acts (Фаза 2.1 #4).
 * Workflow buttons + кнопка «Згенерувати з реєстрів».
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { reconciliationActs } from '@/api/settlements';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import RequirePermission from '@/auth/RequirePermission';
import { validateRequired } from '@/utils/validateRequired';
import { extractApiError } from '@/utils/extractApiError';
import DatePopover from '@/components/DatePopover';
import RequiredLabel from '@/components/RequiredLabel';

const PERMISSION = 'documents.reconciliation_acts.create';

interface FormState {
  organization_id: string;
  document_date: string;
  contractor_id: string;
  contract_id: string;
  period_start: string;
  period_end: string;
  opening_balance_their: string;
  closing_balance_their: string;
  comment: string;
}

const EMPTY: FormState = {
  organization_id: '',
  document_date: new Date().toISOString().slice(0, 16),
  contractor_id: '',
  contract_id: '',
  period_start: new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10),
  period_end: new Date().toISOString().slice(0, 10),
  opening_balance_their: '',
  closing_balance_their: '',
  comment: '',
};

const STATE_LABEL: Record<string, string> = {
  DRAFT: 'Чернетка', SENT: 'Відправлено', AGREED: 'Узгоджено', DISPUTED: 'Спірний',
};

export default function ReconciliationActEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const { availableOrgs } = usePermissionsByOrg(PERMISSION);
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const docQuery = useQuery({
    queryKey: ['reconciliation-act', id],
    queryFn: () => reconciliationActs.get(id!),
    enabled: !isNew,
  });

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_date: d.document_date.slice(0, 16),
        contractor_id: d.contractor_id,
        contract_id: d.contract_id ?? '',
        period_start: d.period_start,
        period_end: d.period_end,
        opening_balance_their: d.opening_balance_their ?? '',
        closing_balance_their: d.closing_balance_their ?? '',
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data]);

  const state = docQuery.data?.state ?? 'DRAFT';
  const isPosted = docQuery.data?.posted ?? false;

  const createFromRegisters = useMutation({
    mutationFn: () => reconciliationActs.fromRegisters({
      organization_id: form.organization_id,
      contractor_id: form.contractor_id,
      contract_id: form.contract_id || null,
      period_start: form.period_start,
      period_end: form.period_end,
      document_date: new Date(form.document_date).toISOString(),
      comment: form.comment || null,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['reconciliation-acts'] });
      navigate(`/settlements/reconciliation-acts/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const confirmFromContractor = useMutation({
    mutationFn: () => reconciliationActs.confirmFromContractor(id!, {
      opening_balance_their: form.opening_balance_their,
      closing_balance_their: form.closing_balance_their,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reconciliation-act', id] }),
    onError: (e) => setError(extractApiError(e)),
  });

  const sendToContractor = useMutation({
    mutationFn: () => reconciliationActs.sendToContractor(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reconciliation-act', id] }),
    onError: (e) => setError(extractApiError(e)),
  });
  const markAgreed = useMutation({
    mutationFn: () => reconciliationActs.markAgreed(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reconciliation-act', id] }),
    onError: (e) => setError(extractApiError(e)),
  });
  const postDoc = useMutation({
    mutationFn: () => reconciliationActs.post(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reconciliation-act', id] }),
    onError: (e) => setError(extractApiError(e)),
  });

  const handleGenerate = () => {
    setError(null);
    const err = validateRequired(
      { organization_id: form.organization_id, contractor_id: form.contractor_id },
      { organization_id: 'Юрособа', contractor_id: 'Контрагент' },
    );
    if (err) { setError(err); return; }
    createFromRegisters.mutate();
  };

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий акт звірки' : `Акт звірки ${docQuery.data?.document_number}`}
          {!isNew && (
            <span style={{ marginLeft: '1rem', fontSize: '0.9rem', color: '#64748b' }}>
              {STATE_LABEL[state]}
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/settlements/reconciliation-acts')}>Назад</button>
          {isNew && (
            <RequirePermission code={PERMISSION}>
              <button className="primary" onClick={handleGenerate} disabled={createFromRegisters.isPending}>
                Згенерувати з реєстрів
              </button>
            </RequirePermission>
          )}
          {!isNew && state === 'DRAFT' && (
            <button onClick={() => sendToContractor.mutate()}>Відправити контрагенту</button>
          )}
          {!isNew && state === 'SENT' && (
            <button onClick={() => markAgreed.mutate()}>Узгоджено</button>
          )}
          {!isNew && !isPosted && (
            <button className="primary" onClick={() => postDoc.mutate()}>Провести</button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <RequiredLabel>Юрособа</RequiredLabel>
          {isNew ? (
            <select value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}>
              <option value="">—</option>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
              ))}
            </select>
          ) : (
            <div>{orgList.data?.items.find((o) => o.id === form.organization_id)?.name ?? form.organization_id}</div>
          )}
        </div>
        <div className="field">
          <label>Дата документа</label>
          <DatePopover withTime value={form.document_date}
            disabled={!isNew}
            onChange={(v) => setForm({ ...form, document_date: v })} />
        </div>
        <div className="field">
          <RequiredLabel>Контрагент (UUID)</RequiredLabel>
          <input value={form.contractor_id}
            disabled={!isNew}
            onChange={(e) => setForm({ ...form, contractor_id: e.target.value })} />
        </div>
        <div className="field">
          <label>Договір (UUID, опц.)</label>
          <input value={form.contract_id}
            disabled={!isNew}
            onChange={(e) => setForm({ ...form, contract_id: e.target.value })} />
        </div>
        <div className="field">
          <label>Період з</label>
          <DatePopover value={form.period_start}
            disabled={!isNew}
            onChange={(v) => setForm({ ...form, period_start: v })} />
        </div>
        <div className="field">
          <label>по</label>
          <DatePopover value={form.period_end}
            disabled={!isNew}
            onChange={(v) => setForm({ ...form, period_end: v })} />
        </div>
      </div>

      {!isNew && (
        <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#f8fafc', borderRadius: 6 }}>
          <strong>Наші дані</strong> (з реєстрів):
          <div>Opening: {docQuery.data?.opening_balance_our}, Closing: <b>{docQuery.data?.closing_balance_our}</b></div>
          <div style={{ marginTop: '0.75rem' }}>
            <strong>Дані контрагента</strong> (введіть з акту):
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginTop: '0.25rem' }}>
            <div className="field">
              <label>Opening (контр.)</label>
              <input type="number" step="0.0001" value={form.opening_balance_their}
                disabled={state === 'AGREED'}
                onChange={(e) => setForm({ ...form, opening_balance_their: e.target.value })} />
            </div>
            <div className="field">
              <label>Closing (контр.)</label>
              <input type="number" step="0.0001" value={form.closing_balance_their}
                disabled={state === 'AGREED'}
                onChange={(e) => setForm({ ...form, closing_balance_their: e.target.value })} />
            </div>
          </div>
          {state !== 'AGREED' && (
            <button style={{ marginTop: '0.5rem' }} onClick={() => confirmFromContractor.mutate()}>
              Зберегти дані контрагента
            </button>
          )}
          <div style={{ marginTop: '0.5rem', color: parseFloat(docQuery.data?.discrepancy_amount ?? '0') === 0 ? '#15803d' : '#b91c1c' }}>
            <strong>Розходження:</strong> {docQuery.data?.discrepancy_amount}
          </div>
        </div>
      )}

      {error && (
        <div className="error" style={{ marginTop: '1rem', color: '#b91c1c' }}>{error}</div>
      )}
    </div>
  );
}
